import { cn } from '../cn.js';

export type ProgressTone = 'brand' | 'success' | 'warning' | 'danger';

const TONES: Record<ProgressTone, string> = {
  brand: 'bg-brand',
  success: 'bg-success-fg',
  warning: 'bg-warning-fg',
  danger: 'bg-danger-fg',
};

export interface ProgressBarProps {
  /** 0-100. Pass null for work whose total is unknown; renders indeterminate. */
  value: number | null;
  tone?: ProgressTone;
  label: string;
  className?: string;
}

export function ProgressBar({ value, tone = 'brand', label, className }: ProgressBarProps) {
  const indeterminate = value === null;
  const clamped = indeterminate ? 0 : Math.min(100, Math.max(0, value));

  return (
    <div
      className={cn('w-full h-1.5 bg-line rounded-full overflow-hidden', className)}
      role="progressbar"
      aria-label={label}
      // Omitting aria-valuenow is what tells assistive technology the value is
      // indeterminate. Setting it to 0 would announce "0 percent" instead.
      {...(indeterminate
        ? {}
        : { 'aria-valuenow': clamped, 'aria-valuemin': 0, 'aria-valuemax': 100 })}
    >
      <div
        className={cn(
          'h-1.5 rounded-full transition-all duration-300',
          TONES[tone],
          indeterminate && 'animate-pulse w-1/3',
        )}
        style={indeterminate ? undefined : { width: `${clamped}%` }}
      />
    </div>
  );
}

export interface ProgressRowProps {
  label: string;
  value: number | null;
  tone?: ProgressTone;
  /** Right-hand readout, e.g. "48%" or an em dash when unknown. */
  readout?: string;
}

/** Label, bar and numeric readout: the campaign row from the reference. */
export function ProgressRow({ label, value, tone = 'brand', readout }: ProgressRowProps) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1">
        <ProgressBar value={value} tone={tone} label={label} />
      </div>
      <span className="text-xs text-muted tabular-nums w-10 text-right">
        {readout ?? (value === null ? '\u2014' : `${Math.round(value)}%`)}
      </span>
    </div>
  );
}
